import { LoadError } from '@/components/LoadError';
import { Onboarding } from '@/components/Onboarding';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { addDays, dayLoad, groupToday, type Task } from '@horizon/shared';
import { EmptyState } from '@/components/EmptyState';
import { ProjectQuickAdd as QuickAdd } from '@/components/ProjectQuickAdd';
import { DayLoad } from '@/components/DayLoad';
import { TaskSection } from '@/components/TaskSection';
import { ShortcutsDialog } from '@/components/ShortcutsDialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/toast-context';
import { useShortcuts } from '@/hooks/useShortcuts';
import { useDeferTasks, useMe, useQuickAdd, useSetTaskStatus, useTasks } from '@/lib/queries';

export default function TodayPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const me = useMe();
  const tasks = useTasks();
  const quickAdd = useQuickAdd();
  const setStatus = useSetTaskStatus();
  const defer = useDeferTasks();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const quickAddRef = useRef<HTMLInputElement>(null);

  const today = me.data?.today ?? '';
  const groups = useMemo(() => groupToday(tasks.data ?? [], today), [tasks.data, today]);
  const load = useMemo(
    () => dayLoad([...groups.today, ...groups.carriedOver], me.data?.dailyLoadMinutes ?? 0),
    [groups, me.data?.dailyLoadMinutes],
  );

  const visible = [...groups.overdue, ...groups.carriedOver, ...groups.today, ...groups.done];

  const toggle = (task: Task) => {
    const next = task.status === 'done' ? 'todo' : 'done';
    setStatus.mutate({ id: task.id, status: next });
    toast.show({
      message: next === 'done' ? t('task.doneToast', { title: task.title }) : t('task.todoToast'),
      actionLabel: t('common.undo'),
      onAction: () => setStatus.mutate({ id: task.id, status: task.status }),
    });
  };

  const deferOne = (task: Task) => {
    const previous = task.scheduledFor;
    defer.mutate({ ids: [task.id], scheduledFor: today });
    toast.show({
      message: t('task.deferredToast'),
      actionLabel: t('common.undo'),
      onAction: () => previous && defer.mutate({ ids: [task.id], scheduledFor: previous }),
    });
  };

  const deferToTomorrow = (task: Task) => {
    const previous = task.scheduledFor;
    defer.mutate({ ids: [task.id], scheduledFor: addDays(today, 1) });
    toast.show({
      message: t('task.deferredTomorrowToast'),
      actionLabel: t('common.undo'),
      onAction: () => previous && defer.mutate({ ids: [task.id], scheduledFor: previous }),
    });
  };

  const moveSelection = (step: number) => {
    if (visible.length === 0) return;
    const index = visible.findIndex((task) => task.id === selectedId);
    const next = index < 0 ? 0 : Math.min(visible.length - 1, Math.max(0, index + step));
    setSelectedId(visible[next]?.id ?? null);
  };

  const selected = visible.find((task) => task.id === selectedId);

  useShortcuts(
    {
      n: () => quickAddRef.current?.focus(),
      '/': () => quickAddRef.current?.focus(),
      '?': () => setHelpOpen(true),
      j: () => moveSelection(1),
      k: () => moveSelection(-1),
      x: () => selected && toggle(selected),
      t: () => selected && selected.status === 'todo' && deferToTomorrow(selected),
      Escape: () => setSelectedId(null),
    },
    {
      'g t': () => navigate('/'),
      'g w': () => navigate('/week'),
      'g i': () => navigate('/inbox'),
      'g c': () => navigate('/goals'),
      'g s': () => navigate('/settings'),
    },
  );

  const carryAll = () => {
    const ids = groups.carriedOver.map((task) => task.id);
    if (ids.length === 0) return;
    const previous = new Map(groups.carriedOver.map((task) => [task.id, task.scheduledFor]));
    defer.mutate({ ids, scheduledFor: today });
    toast.show({
      message: t('today.carriedToast', { count: ids.length }),
      actionLabel: t('common.undo'),
      onAction: () => {
        for (const [id, date] of previous) {
          if (date) defer.mutate({ ids: [id], scheduledFor: date });
        }
      },
    });
  };

  const nothingToShow = !tasks.isLoading && visible.length === 0 && !tasks.isError && !me.isError;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="font-serif text-2xl font-semibold">{t('today.title')}</h1>
        <DayLoad load={load} />
      </div>

      <Onboarding />
      <QuickAdd
        ref={quickAddRef}
        today={today}
        pending={quickAdd.isPending}
        onSubmit={(input, parse, projectId) =>
          quickAdd.mutateAsync({ input, parse, projectId: projectId ?? null })
        }
      />

      {(tasks.isError || me.isError) && (
        <LoadError retry={() => Promise.all([tasks.refetch(), me.refetch()])} />
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))] items-start gap-6">
        <TaskSection
          title={t('today.overdue')}
          hint={t('today.overdueHint')}
          tone="attention"
          tasks={groups.overdue}
          today={today}
          selectedId={selectedId}
          onToggle={toggle}
          onDefer={deferOne}
        />

        <TaskSection
          title={t('today.carriedOver')}
          tasks={groups.carriedOver}
          today={today}
          selectedId={selectedId}
          onToggle={toggle}
          onDefer={deferOne}
          action={
            <Button size="sm" variant="outline" onClick={carryAll}>
              {t('today.carryAll')}
            </Button>
          }
        />

        <TaskSection
          title={t('today.planned')}
          tasks={groups.today}
          today={today}
          selectedId={selectedId}
          onToggle={toggle}
          onDefer={deferToTomorrow}
        />

        <TaskSection
          title={t('today.completed')}
          tasks={groups.done}
          today={today}
          selectedId={selectedId}
          onToggle={toggle}
          onDefer={deferOne}
        />
      </div>
      {nothingToShow && <EmptyState titleKey="today.empty.title" hintKey="today.empty.hint" />}

      <ShortcutsDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </section>
  );
}
